import { UnionPaymentKind, UnionPaymentUsecase, UserDefinedRow } from "../../model";
import { DatelyObject, Years, Dates } from "../../util";
import { UsecaseValidationMessage } from "../UsecaseValidationMessage";
import { BaseUsecaseLogic } from "../BaseUsecaseLogic";
import { RowUsecaseCalcData } from "../RowUsecaseCalcData";

/** Client-side logic for UnionPaymentUsecase  */
export class UnionPaymentUsecaseLogic extends BaseUsecaseLogic {

  /**
   * Updates the usecase values based on user input without going to the server.
   * @param row Row to update.
   * @param date The reference date for which the logic is done.
   * Typically the salary date of a calculation and the typically, the information needed is just the year for which the yearly changing numbers are fetched, but may later have more extended features.
   * @param calcData The calculation related data that is required by the more complex calculations:
   * This data is available only after first round-trip to the server (results object exists).
   */
  public updateUsecase(row: UserDefinedRow, date?: DatelyObject, calcData?: RowUsecaseCalcData): void {
    const data: UnionPaymentUsecase = row.data ?? {};
    data.kind = (data.kind || UnionPaymentKind.Percentage);

    if (!data.kind || data.kind === UnionPaymentKind.Undefined) {
      data.kind = UnionPaymentKind.Percentage;
    }

    if (data.kind === UnionPaymentKind.Other) {
      if ((data as any).isFixedSum) {
        data.kind = UnionPaymentKind.Fixed;
        row.price = (row.price ?? 0) * (row.count ?? 1);
        row.count = 1;
      } else {
        data.kind = UnionPaymentKind.Percentage;
      }
    }

    const numbers = Years.getYearlyChangingNumbers(date);
    const raksaIban = "FI6115553000120981";
    const raksaName = "Rakennusliitto";
    const totalTaxable = calcData?.totalTaxable ?? 0;

    switch (data.kind) {
      case UnionPaymentKind.Fixed:
        row.count = 1;
        if (row.price === totalTaxable) {
          row.price = 0;
        }
        // row.price
        break;
      case UnionPaymentKind.Percentage:
        if (!row.count || row.count >= 1) {
          row.count = 0;
        }
        row.price = totalTaxable;
        break;
      case UnionPaymentKind.PercentageAndLimits:
        if (data.paymentPercentage && data.paymentPercentage >= 1) {
          data.paymentPercentage = 0;
          row.count = 0;
          row.price = totalTaxable;
        }
        else {
          const payment = (data.paymentPercentage ?? 0) * (totalTaxable ?? 0);
          if (data.minimumPayment &&
            payment < data.minimumPayment) {
            row.count = 1;
            row.price = data.minimumPayment;
          } else if (
            data.maximumPayment &&
            payment > data.maximumPayment) {
            row.count = 1;
            row.price = data.maximumPayment;
          } else {
            row.count = data.paymentPercentage;
            row.price = totalTaxable;
          }
        }
        break;
      case UnionPaymentKind.RaksaNormal:
        data.iban = raksaIban;
        data.recipientFullName = raksaName;
        row.count = numbers?.sideCosts?.unionPaymentRaksaA;
        if (totalTaxable != null) {
          row.price = totalTaxable;
        }
        break;
      case UnionPaymentKind.RaksaUnemploymentOnly:
        data.iban = raksaIban;
        data.recipientFullName = raksaName;
        row.count = numbers?.sideCosts?.unionPaymentRaksaAoTa;
        if (totalTaxable != null) {
          row.price = totalTaxable;
        }

        break;
    }
    this.setReferenceNumber(data, calcData?.employerOfficialId, calcData?.workerOfficialId,date);
  }

  /** Gets validation message for UnionPaymentUsecase  */
  public getUcValidation(row: UserDefinedRow): UsecaseValidationMessage {
    const data: UnionPaymentUsecase = row.data || {};

    if (data.kind === UnionPaymentKind.Other) {
      if ((data as any).isFixedSum) {
        data.kind = UnionPaymentKind.Fixed;
        row.price = (row.price ?? 0) * (row.count ?? 1);
        row.count = 1;
      } else {
        data.kind = UnionPaymentKind.Percentage;
      }
    }

    switch (data.kind) {
      case UnionPaymentKind.Fixed:
        if (!row.price) {
          return {
            type: "error",
            msg: "SALAXY.VALIDATION.Usecases.unionPayment.fixedError",
          };
        }
        return {
          type: "default",
          msg: "SALAXY.VALIDATION.Usecases.unionPayment.fixed",
        };
      case UnionPaymentKind.Percentage:
        if (!row.count) {
          return {
            type: "error",
            msg: "SALAXY.VALIDATION.Usecases.unionPayment.percentageError",
          };
        }
        return {
          type: "default",
          msg: "SALAXY.VALIDATION.Usecases.unionPayment.percentage",
        };
      case UnionPaymentKind.PercentageAndLimits:
        if (!data?.paymentPercentage) {
          return {
            type: "error",
            msg: "SALAXY.VALIDATION.Usecases.unionPayment.percentageError",
          };
        }
        return {
          type: "default",
          msg: "SALAXY.VALIDATION.Usecases.unionPayment.percentage",
        };
      case UnionPaymentKind.RaksaNormal:
      case UnionPaymentKind.RaksaUnemploymentOnly:
        if (!row.count) {
          return {
            type: "error",
            msg: "SALAXY.VALIDATION.Usecases.unionPayment.raksaError",
          };
        }
        return {
          type: "default",
          msg: "SALAXY.VALIDATION.Usecases.unionPayment.raksa",
        };
      default:
        return {
          type: "error",
          msg: "SALAXY.VALIDATION.Usecases.unionPayment.default",
        };
    }
  }

  private setReferenceNumber(usecase: UnionPaymentUsecase, officialId: string | undefined | null, ssn: string | undefined | null, salaryDate: DatelyObject | undefined) {
    if (!salaryDate || !officialId || officialId.length > 9 || !ssn) {
      return;
    }
    // ssn: ddMMyy-xxxX
    // reference: 8{yy}{MM}{d10}{xxx}{d1}{salaryMonth}{officialId without check number}{check number for reference}
    const referenceBase = `8${ssn.substring(4, 6)}${ssn.substring(2, 4)}${ssn.substring(0, 1)}${ssn.substring(7, 10)}${ssn.substring(1, 2)}${Dates.format(salaryDate, "MM")}${officialId.substring(0, 7)}`;
    const referenceNumber = `${referenceBase}${this.getCheckNumber(referenceBase)}`;
    if (!usecase.referenceNumber ||
      (
        usecase.referenceNumber.length == 20 &&
        usecase.referenceNumber.substring(0, 10) == referenceNumber.substring(0, 10) &&
        usecase.referenceNumber.substring(12, 19) == referenceNumber.substring(12, 19)
      )
    ) {
      usecase.referenceNumber = referenceNumber;
    }
  }

  private getCheckNumber(numberWithoutTheCheckNumber: string): string {
    if (!numberWithoutTheCheckNumber) {
      return "";
    }
    const multipliers = [7, 3, 1];
    const reverse = numberWithoutTheCheckNumber.split("").reverse();

    let sum = 0;
    for (let i = 0; i < reverse.length; i++) {
      sum += Number.parseInt(reverse[i]) * multipliers[i % 3];
    }
    return `${((10 - (sum % 10)) % 10)}`;
  }
}
